import dayjs from "dayjs";
import { LloFunnelReportItem } from "../../../../api";
import { CASE_LEVEL_COLUMNS } from "./constants";
import { stripHtml } from "./htmlUtils";
import type { RowWithSpans, SortableColumn, SortDirection } from "./types";

function getMainFunnelTabOrder(funnel?: string | null): number {
  const value = funnel ?? "";
  const prefix = "Main Funnel — ";
  if (!value.startsWith(prefix)) {
    return 99;
  }

  const tab = value.slice(prefix.length);
  const order: Record<string, number> = {
    Complaint: 0,
    "Discovery Requests": 1,
    "Plaintiff's Discovery Responses": 2,
    "Judicial Assignment Responsive": 3,
    "Case Management Conference": 4,
  };

  return order[tab] ?? 99;
}

function getFunnelCategoryOrder(funnel?: string | null): number {
  const value = funnel ?? "";
  if (value.startsWith("Main Funnel")) return 0;
  if (value.startsWith("Secondary Funnel")) return 1;
  if (value.startsWith("Single Task")) return 2;
  if (value === "Manual") return 3;
  return 4;
}

function getSortValue(row: LloFunnelReportItem, column: SortableColumn): string | number {
  switch (column) {
    case "caseName":
      return row.caseName?.toLowerCase() ?? "";
    case "raa":
      return row.raa?.toLowerCase() ?? "";
    case "ap":
      return row.ap?.toLowerCase() ?? "";
    case "upcomingDeadlines":
      return row.upcomingDeadlines?.toLowerCase() ?? "";
    case "lloExpiration":
      return row.lloExpiration?.toLowerCase() ?? "";
    case "negotiation":
      return row.negotiation?.toLowerCase() ?? "";
    case "funnel":
      return `${getFunnelCategoryOrder(row.funnel)}|${getMainFunnelTabOrder(row.funnel)}|${row.funnel?.toLowerCase() ?? ""}`;
    case "taskName":
      return row.taskName?.toLowerCase() ?? "";
    case "taskAssignee":
      return row.taskAssignee?.toLowerCase() ?? "";
    case "description":
      return stripHtml(row.description ?? "").toLowerCase();
    case "dueDate":
      return row.dueDate ? dayjs(row.dueDate).valueOf() : Number.MAX_SAFE_INTEGER;
    default:
      return "";
  }
}

function compareSortValues(
  aValue: string | number,
  bValue: string | number,
  order: SortDirection
) {
  if (aValue < bValue) return order === "asc" ? -1 : 1;
  if (aValue > bValue) return order === "asc" ? 1 : -1;
  return 0;
}

function getDefaultTaskOrder(a: LloFunnelReportItem, b: LloFunnelReportItem) {
  const categoryOrder = getFunnelCategoryOrder(a.funnel) - getFunnelCategoryOrder(b.funnel);
  if (categoryOrder !== 0) return categoryOrder;

  const tabOrder = getMainFunnelTabOrder(a.funnel) - getMainFunnelTabOrder(b.funnel);
  if (tabOrder !== 0) return tabOrder;

  const funnelCompare = (a.funnel ?? "").localeCompare(b.funnel ?? "");
  if (funnelCompare !== 0) return funnelCompare;

  const aDue = a.dueDate ? dayjs(a.dueDate).valueOf() : Number.MAX_SAFE_INTEGER;
  const bDue = b.dueDate ? dayjs(b.dueDate).valueOf() : Number.MAX_SAFE_INTEGER;
  return aDue - bDue;
}

function compareTaskRows(
  a: LloFunnelReportItem,
  b: LloFunnelReportItem,
  orderBy: SortableColumn,
  order: SortDirection
) {
  const primary = compareSortValues(getSortValue(a, orderBy), getSortValue(b, orderBy), order);
  if (primary !== 0) return primary;
  return getDefaultTaskOrder(a, b);
}

function getGroupSortValue(group: LloFunnelReportItem[], orderBy: SortableColumn): string | number {
  const first = group[0];
  if (!first) return "";

  if (CASE_LEVEL_COLUMNS.includes(orderBy)) {
    return getSortValue(first, orderBy);
  }

  if (orderBy === "dueDate") {
    return Math.min(
      ...group.map((row) => (row.dueDate ? dayjs(row.dueDate).valueOf() : Number.MAX_SAFE_INTEGER))
    );
  }

  return group.reduce<string | number>((best, row) => {
    const value = getSortValue(row, orderBy);
    if (best === "") return value;
    return compareSortValues(value, best, "asc") < 0 ? value : best;
  }, "");
}

export function groupRowsByCase(rows: LloFunnelReportItem[]): LloFunnelReportItem[][] {
  const groups: LloFunnelReportItem[][] = [];
  let currentGroup: LloFunnelReportItem[] = [];
  let currentCaseName = "";

  rows.forEach((row) => {
    if (row.caseName !== currentCaseName) {
      if (currentGroup.length > 0) {
        groups.push(currentGroup);
      }
      currentGroup = [row];
      currentCaseName = row.caseName ?? "";
    } else {
      currentGroup.push(row);
    }
  });

  if (currentGroup.length > 0) {
    groups.push(currentGroup);
  }

  return groups;
}

export function sortReportRows(
  rows: LloFunnelReportItem[],
  orderBy: SortableColumn,
  order: SortDirection
): LloFunnelReportItem[] {
  const groups = groupRowsByCase(rows);

  groups.sort((groupA, groupB) => {
    const primary = compareSortValues(
      getGroupSortValue(groupA, orderBy),
      getGroupSortValue(groupB, orderBy),
      order
    );
    if (primary !== 0) return primary;
    return (groupA[0]?.caseName ?? "").localeCompare(groupB[0]?.caseName ?? "");
  });

  return groups.flatMap((group) =>
    [...group].sort((a, b) =>
      CASE_LEVEL_COLUMNS.includes(orderBy)
        ? getDefaultTaskOrder(a, b)
        : compareTaskRows(a, b, orderBy, order)
    )
  );
}

export function computeCaseGroupSpans(rows: LloFunnelReportItem[]): RowWithSpans[] {
  const out: RowWithSpans[] = [];
  let i = 0;
  let caseNumber = 0;

  while (i < rows.length) {
    const caseName = rows[i]?.caseName ?? "";
    let j = i + 1;
    while (j < rows.length && (rows[j]?.caseName ?? "") === caseName) j++;

    const span = j - i;
    caseNumber += 1;

    for (let k = i; k < j; k++) {
      out.push({
        ...rows[k],
        rowNumber: k === i ? caseNumber : undefined,
        groupRowSpan: k === i ? span : undefined,
        skipCaseColumns: k !== i,
        isLastInCaseGroup: k === j - 1,
      });
    }

    i = j;
  }

  return out;
}
